<template>
  <div>
    <div class="container">
      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="page-title">MCP API Key</h3>
        <div class="d-flex ga-2">
          <v-btn
            variant="outlined"
            @click="showSetupDialog = true"
            prepend-icon="mdi-help-circle-outline"
          >
            Setup Instructions
          </v-btn>
          <v-btn
            variant="outlined"
            @click="showExpiryDialog = true"
            prepend-icon="mdi-key-plus"
          >
            {{ stateUser?.hasMcpApiKey ? 'Regenerate API Key' : 'Generate API Key' }}
          </v-btn>
        </div>
      </div>

      <v-alert
        v-if="!stateUser?.hasMcpApiKey && !generatedApiKey"
        type="info"
        variant="tonal"
        text="No API key generated yet. Click 'Generate API Key' to create one for MCP integration."
        border="start"
      />

      <v-card v-else-if="generatedApiKey" class="api-key-card" variant="outlined">
        <div class="api-key-card-header">
          <v-icon size="20" class="mr-2">mdi-key-variant</v-icon>
          <span class="font-weight-medium">Your API Key</span>
          <v-chip size="x-small" color="success" variant="flat" class="ml-2">New</v-chip>
        </div>

        <v-card-text class="pt-0">
          <div class="api-key-display">
            <code class="api-key-value">{{ generatedApiKey }}</code>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              @click="onCopyApiKey"
              class="copy-btn"
            >
              <v-icon size="16" class="mr-1">mdi-content-copy</v-icon>
              Copy
            </v-btn>
          </div>

          <div class="api-key-warning mt-3">
            <v-icon size="16" color="amber-darken-2" class="mr-1">mdi-alert-circle-outline</v-icon>
            <span class="text-body-2 text-medium-emphasis">
              Save this key now. You won't be able to see it again.
            </span>
          </div>

          <div class="api-key-warning mt-1">
            <v-icon size="16" class="mr-1">mdi-calendar-clock</v-icon>
            <span class="text-body-2 text-medium-emphasis">
              {{ generatedExpiryLabel }}
            </span>
          </div>
        </v-card-text>
      </v-card>

      <v-list v-else-if="stateUser?.hasMcpApiKey" lines="two" class="rounded-lg pa-0">
        <v-list-item>
          <template #prepend>
            <v-icon :color="isApiKeyExpired ? 'warning' : 'success'">
              {{ isApiKeyExpired ? 'mdi-alert-circle' : 'mdi-check-circle' }}
            </v-icon>
          </template>

          <v-list-item-title>
            {{ isApiKeyExpired ? 'API Key Expired' : 'API Key Configured' }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-wrap">
            {{ apiKeyStatusText }}
            <template v-if="!isApiKeyExpired">
              <br />
              <span class="text-caption text-medium-emphasis">Note: Regenerating will invalidate your current key.</span>
            </template>
          </v-list-item-subtitle>

          <template #append>
            <v-btn
              size="small"
              color="error"
              variant="tonal"
              prepend-icon="mdi-close"
              :loading="revokeLoading"
              @click="showRevokeConfirm = true"
            >
              Revoke
            </v-btn>
          </template>
        </v-list-item>
      </v-list>

      <v-divider class="my-8" />

      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="page-title">Connected MCP Clients</h3>
      </div>

      <v-progress-linear
        v-if="!connectionsLoaded"
        indeterminate
        color="primary"
        class="mb-2"
      />

      <v-alert
        v-else-if="connectionsLoaded && connectedMcpClients.length === 0"
        type="info"
        variant="tonal"
        :text="`No MCP clients are currently connected. When you sign in to ${brand.name} from Claude Web, ChatGPT, or another MCP-compatible client, the connection will appear here.`"
        border="start"
        class="mb-4"
      />

      <v-list v-else lines="two" class="rounded-lg">
        <v-list-item
          v-for="connection in connectedMcpClients"
          :key="connection.clientId"
        >
          <template #prepend>
            <v-icon color="primary">
              mdi-link-variant
            </v-icon>
          </template>

          <v-list-item-title>
            {{ connection.clientName || connection.clientId }}
          </v-list-item-title>
          <v-list-item-subtitle>
            <span v-if="connection.connectedAt">
              Connected {{ formatConnectionDate(connection.connectedAt) }}
            </span>
            <span v-if="connection.scope" class="text-caption ml-2 text-medium-emphasis">
              · {{ connection.scope }}
            </span>
          </v-list-item-subtitle>

          <template #append>
            <v-btn
              size="small"
              color="error"
              variant="tonal"
              prepend-icon="mdi-close"
              :loading="revokingClientId === connection.clientId"
              @click="() => onRevokeMcpConnection(connection)"
            >
              Disconnect
            </v-btn>
          </template>
        </v-list-item>
      </v-list>

      <v-divider class="my-8" />

      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="page-title">Connected GitHub Accounts</h3>
        <v-btn
          variant="outlined"
          :loading="loading"
          @click="onConnect"
          prepend-icon="mdi-plus"
        >
          Connect
        </v-btn>
      </div>

      <div v-if="!stateUser?.githubAccounts?.length">
        <v-alert
          type="info"
          variant="tonal"
          text="No GitHub accounts are connected."
          border="start"
          class="mb-4"
        />
      </div>
      <v-list v-else lines="one" class="rounded-lg">
        <v-list-item
          v-for="githubAccount in stateUser?.githubAccounts"
          :key="githubAccount.login"
        >
          <!-- Avatar -->
          <template #prepend>
            <v-avatar size="32" class="mr-2">
              <v-img
                :src="githubAccount.avatar"
                alt="GitHub Avatar"
              />
            </v-avatar>
          </template>

          <!-- Username -->
          <v-list-item-title>GitHub</v-list-item-title>
          <v-list-item-subtitle>
            <span>@{{ githubAccount.login }}</span>
          </v-list-item-subtitle>

          <!-- Actions -->
          <template #append>
            <div class="d-flex align-center ga-2">
              <v-btn
                size="small"
                color="error"
                variant="tonal"
                @click="() => onDisconnect(githubAccount.login)"
                prepend-icon="mdi-close"
                :loading="loading"
              >
                Disconnect
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </div>

    <McpSetupDialog v-model="showSetupDialog" />

    <McpApiKeyExpiryDialog
      v-model="showExpiryDialog"
      :current-key-state="currentKeyState"
      :loading="apiKeyLoading"
      @generate="onGenerateApiKey"
    />

    <ConfirmationDialog
      v-model="showRevokeConfirm"
      title="Revoke API key?"
      text="Your current API key stops working and cannot be restored. Any MCP client still using it loses access within a minute."
      confirm-label="Revoke"
      @confirm="onRevokeApiKey"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import gitHubApi from '@/api/gitHubApi'
import McpSetupDialog from '@/components/McpSetupDialog.vue'
import McpApiKeyExpiryDialog from '@/components/McpApiKeyExpiryDialog.vue'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import client from '@/graphql/client'
import ListConnectedMcpClients from '@/graphql/queries/ListConnectedMcpClients'
import RevokeMcpClientConnection from '@/graphql/mutations/RevokeMcpClientConnection'
import { useBrand } from '@/composables/useBrand'
import { formatApiKeyExpiry } from '@/utils/apiKeyExpiry'

const EXPIRY_TICK_MS = 60 * 1000

const store = useStore()
const brand = useBrand()
const stateUser = computed(() => store.state.user.user)

const loading = ref(false)
const apiKeyLoading = ref(false)
const revokeLoading = ref(false)
const generatedApiKey = ref(null)
const generatedExpiresAt = ref(null)
const showSetupDialog = ref(false)
const showExpiryDialog = ref(false)
const showRevokeConfirm = ref(false)
const now = ref(Date.now())
let expiryTicker = null

const connectedMcpClients = ref([])
const connectionsLoaded = ref(false)
const revokingClientId = ref(null)

const formatConnectionDate = (iso) => {
  if (!iso) {
    return ''
  }
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const isApiKeyExpired = computed(() => {
  const expiresAt = stateUser.value?.apiKeyExpiresAt

  return Boolean(expiresAt) && new Date(expiresAt).getTime() <= now.value
})

const currentKeyState = computed(() => {
  if (!stateUser.value?.hasMcpApiKey) {
    return null
  }

  return isApiKeyExpired.value ? 'expired' : 'active'
})

const apiKeyStatusText = computed(() => {
  const expiresAt = stateUser.value?.apiKeyExpiresAt

  if (!expiresAt) {
    return 'Your MCP API key is active and never expires.'
  }

  if (isApiKeyExpired.value) {
    return `This key expired on ${formatApiKeyExpiry(expiresAt)}. Regenerate to get a new one.`
  }

  return `Your MCP API key is active. It expires on ${formatApiKeyExpiry(expiresAt)}.`
})

const generatedExpiryLabel = computed(() => {
  if (!generatedExpiresAt.value) {
    return 'This key never expires.'
  }

  return `Expires on ${formatApiKeyExpiry(generatedExpiresAt.value)}`
})

const loadConnectedMcpClients = async () => {
  try {
    const res = await client.query({
      operationName: 'listConnectedMcpClients',
      query: ListConnectedMcpClients
    })
    connectedMcpClients.value = res.listConnectedMcpClients || []
  } catch (e) {
    store.commit('snackbar/showMessage', {
      content: `Could not load connected MCP clients: ${e.message}`,
      timeout: 4000,
      color: 'error'
    }, { root: true })
  } finally {
    connectionsLoaded.value = true
  }
}

const onRevokeMcpConnection = async (connection) => {
  revokingClientId.value = connection.clientId
  try {
    await client.query({
      operationName: 'revokeMcpClientConnection',
      query: RevokeMcpClientConnection,
      variables: { clientId: connection.clientId }
    })
    connectedMcpClients.value = connectedMcpClients.value.filter((c) => c.clientId !== connection.clientId)
    store.commit('snackbar/showMessage', {
      content: 'MCP client disconnected.',
      timeout: 3000,
      color: 'success'
    }, { root: true })
  } catch (e) {
    store.commit('snackbar/showMessage', {
      content: `Could not disconnect MCP client: ${e.message}`,
      timeout: 4000,
      color: 'error'
    }, { root: true })
  } finally {
    revokingClientId.value = null
  }
}

const onGenerateApiKey = async (expiresInDays) => {
  apiKeyLoading.value = true
  generatedApiKey.value = null

  try {
    const result = await store.dispatch('user/generateApiKey', { expiresInDays })

    if (result) {
      generatedApiKey.value = result.apiKey
      generatedExpiresAt.value = result.expiresAt
      showExpiryDialog.value = false
    }
  } finally {
    apiKeyLoading.value = false
  }
}

const onRevokeApiKey = async () => {
  revokeLoading.value = true

  try {
    const revoked = await store.dispatch('user/revokeApiKey')

    if (revoked) {
      generatedApiKey.value = null
      generatedExpiresAt.value = null
    }
  } finally {
    revokeLoading.value = false
  }
}

const onCopyApiKey = async () => {
  if (generatedApiKey.value) {
    try {
      await navigator.clipboard.writeText(generatedApiKey.value)
      store.commit('snackbar/showMessage', {
        content: 'API Key copied to clipboard!',
        timeout: 2000,
        color: 'success'
      }, { root: true })
    } catch (err) {
      store.commit('snackbar/showMessage', {
        content: 'Failed to copy to clipboard. Please select and copy manually.',
        timeout: 3000,
        color: 'error'
      }, { root: true })
    }
  }
}

const onConnect = () => {
  const clientId = process.env.VUE_APP_GITHUB_CLIENT_ID
  const currentOrigin = window.location.origin
  const redirectUri = encodeURIComponent(`${currentOrigin}/github/callback`)
  const scope = 'repo'
  const authUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}`

  window.open(authUrl, '_blank', 'width=600,height=700')

  window.addEventListener('message', async (event) => {
    if (event.origin !== currentOrigin) return // adjust to your domain
    if (event.data.type === 'github-auth') {
      const authCode = event.data.code

      const response = await gitHubApi.saveGitHubToken(authCode)

      if (response.success) {
        await store.dispatch('user/updateUser', { ...stateUser.value, githubAccounts: response.resData.githubAccounts })
      } else {
        store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
      }
    }
  })
}

const onDisconnect = async (username) => {
  const response = await gitHubApi.removeGitHubToken(username)

  if (response.success) {
    await store.dispatch('user/updateUser', { ...stateUser.value, githubAccounts: response.resData.githubAccounts })
  } else {
    store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
  }
}

const updateNow = () => {
  now.value = Date.now()
}

onMounted(() => {
  loadConnectedMcpClients()
  expiryTicker = setInterval(updateNow, EXPIRY_TICK_MS)
})

onUnmounted(() => {
  clearInterval(expiryTicker)
})
</script>

<style scoped>
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.v-btn:not(.v-btn--icon) {
  border-radius: 4px !important;
}

.api-key-card {
  border-radius: 8px;
  border-color: rgba(var(--v-theme-primary), 0.3);
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.02) 0%, rgba(var(--v-theme-primary), 0.05) 100%);
}

.api-key-card-header {
  display: flex;
  align-items: center;
  padding: 16px 16px 12px;
  color: rgb(var(--v-theme-primary));
}

.api-key-display {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 4px 16px;
}

.api-key-value {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  color: var(--text-dark);
  flex: 1;
  word-break: break-all;
  padding: 8px 0;
}

.api-key-display .copy-btn {
  flex-shrink: 0;
}

.api-key-warning {
  display: flex;
  align-items: center;
}
</style>
